<script setup lang="ts">
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { computed, defineProps, defineEmits } from "vue";
import { CheckCircleIcon } from "@heroicons/vue/20/solid";
import { usePlanStore } from "@/stores/planStore.ts";
import { storeToRefs } from "pinia";
import { frequencies } from "@/components/Account/Checkout/types.ts";

const planStore = usePlanStore();
const {
    planFrequencies,
    currentFrequency,
    currentPlan: plan,
} = storeToRefs(planStore);

const props = defineProps<{
    label?: string;
}>();

const emit = defineEmits<{
    change: [value: string];
}>();

const handleChange = async (value: string) => {
    // Update the frequency in the planStore, which will also update the URL and fetch new plan data
    await planStore.updateFrequency(value);
    emit("change", value);
};
</script>

<template>
    <div class="w-full">
        <RadioGroup
            :model-value="currentFrequency"
            @update:model-value="handleChange"
        >
            <RadioGroupLabel
                v-if="props.label"
                class="text-xl font-bold leading-6 text-gray-900"
                >{{ props.label }}
            </RadioGroupLabel>

            <div class="mt-4 flex flex-col xs:flex-row gap-5">
                <RadioGroupOption
                    class="group"
                    as="template"
                    v-for="price in planFrequencies"
                    :key="price.frequency"
                    :value="price.frequency"
                    v-slot="{ checked, active }"
                >
                    <div
                        class="gap-1 items-center relative flex cursor-pointer focus:outline-none"
                    >
                        <div class="relative" aria-hidden="true">
                            <span
                                class="block rounded-full bg-white h-5 w-5"
                                :class="
                                    active
                                        ? 'border-blue-700 border-[2px]'
                                        : 'border-gray-400 border-[1px]'
                                "
                            ></span>
                            <CheckCircleIcon
                                v-if="checked"
                                class="h-5 w-5 text-blue-700 absolute top-0 left-0"
                                aria-hidden="true"
                            />
                        </div>
                        <span class="flex w-full">
                            <span class="flex flex-col w-full">
                                <RadioGroupLabel
                                    as="span"
                                    class="w-full flex flex-row items-center flex-wrap text-sm text-gray-900"
                                >
                                    <span class="mr-1 capitalize">{{
                                        frequencies[price.frequency].alt
                                    }}</span>
                                    <span
                                        v-if="price.badge"
                                        class="text-green-600 text-xs"
                                        >{{ price.badge }}</span
                                    >
                                </RadioGroupLabel>
                            </span>
                        </span>
                    </div>
                </RadioGroupOption>
            </div>
        </RadioGroup>
    </div>
</template>
